import { makeAutoObservable, observable, runInAction } from 'mobx';
import axios from 'axios';
import urls from './Urls';
import audioSettingsStore from './AudioSettingsStore';
import {
    INWORLD_MAX_TEXT_CHARS, INWORLD_SPEAKING_RATE, INWORLD_VOICE_ID_PATTERN, PROVIDER_INWORLD,
    describeError, getMimeFromContentType, providerOfModel
} from './AudioCatalog';

/** The code of the Hebrew option of the test form - the only language that has the niqqud option */
const HEBREW_CODE = 'he-IL';

class GenerateAudioStore {
    text = '';
    prompt = '';
    language = 'en-US';
    name = 'Callirrhoe';
    model = 'gemini-3.8-flash-tts';

    /** true after the user typed in the field - then the value of the automatic settings does not replace it */
    promptTouched = false;

    // The options of Inworld (SCRUM-1847). Inworld takes no prompt: the voice, the pace and the delivery decide how it reads.
    /** the voice id of Inworld; empty - the server uses the voice of the language that is saved in its settings */
    inworldName = '';
    /** a string, as it is typed in the field */
    speakingRate = String(INWORLD_SPEAKING_RATE.default);
    deliveryMode = 'STABLE';
    niqqud = 'keep';
    /** true after the user changed one of the three options above - then the settings of the server do not replace them */
    inworldTouched = false;

    loading = false;
    /**
     * The audio files that were created, the newest first.
     * Each one: { id, url, mime, ms, bytes, text, model, provider, name, language, prompt, createdAt }
     * (Inworld: no prompt, and the name may be missing - then the server used its default voice.)
     */
    generatedAudios = [];
    /** { status, message, detail } of the last creation that failed, or null */
    error = null;
    lastId = 0;

    constructor() {
        makeAutoObservable(this, { generatedAudios: observable.shallow });
    }

    setText = (val) => this.text = val;
    setPrompt = (val) => {
        this.prompt = val;
        this.promptTouched = true;
    };
    setLanguage = (val) => this.language = val;
    setName = (val) => this.name = val;
    setModel = (val) => this.model = val;
    setLoading = (val) => this.loading = val;
    setInworldName = (val) => this.inworldName = val;
    setSpeakingRate = (val) => {
        this.speakingRate = val;
        this.inworldTouched = true;
    };
    setDeliveryMode = (val) => {
        this.deliveryMode = val;
        this.inworldTouched = true;
    };
    setNiqqud = (val) => {
        this.niqqud = val;
        this.inworldTouched = true;
    };

    /** The model that is chosen, as the server describes it (Gemini and Inworld models), or undefined */
    get selectedModel() {
        return audioSettingsStore.allModels.find((model) => model.id === this.model);
    }

    get provider() {
        return providerOfModel(this.selectedModel);
    }

    get isInworld() {
        return this.provider === PROVIDER_INWORLD;
    }

    /** The delivery mode is only for the models that support it (not for Flash) */
    get supportsDeliveryMode() {
        return Boolean(this.isInworld && this.selectedModel?.supportsDeliveryMode);
    }

    get isHebrew() {
        return this.language === HEBREW_CODE;
    }

    /** What is wrong with the options of Inworld ('' - nothing), shown under the fields */
    get speakingRateError() {
        if (!this.isInworld) return '';
        const value = this.speakingRate.trim() === '' ? NaN : Number(this.speakingRate);
        if (!Number.isFinite(value) || value < INWORLD_SPEAKING_RATE.min || value > INWORLD_SPEAKING_RATE.max) {
            return `A number between ${INWORLD_SPEAKING_RATE.min} and ${INWORLD_SPEAKING_RATE.max}`;
        }
        return '';
    }

    get inworldNameError() {
        if (!this.isInworld) return '';
        const name = this.inworldName.trim();
        return name && !INWORLD_VOICE_ID_PATTERN.test(name) ? 'Letters, digits and . _ : - only, no spaces' : '';
    }

    get textError() {
        if (this.isInworld && this.text.length > INWORLD_MAX_TEXT_CHARS) {
            return `${this.text.length} / ${INWORLD_MAX_TEXT_CHARS} characters - Inworld takes at most ${INWORLD_MAX_TEXT_CHARS}`;
        }
        return '';
    }

    /** true - the form has everything that the creation needs */
    get canGenerate() {
        if (this.loading || this.text.trim() === '' || this.textError) return false;
        if (this.isInworld) return !this.speakingRateError && !this.inworldNameError;
        return this.prompt.trim() !== '';
    }

    /** The prompt of the form starts from the automatic settings, unless the user already typed there */
    applyDefaults = (effective) => {
        if (!effective) return;
        if (!this.promptTouched && effective.prompt) this.prompt = effective.prompt;
    };

    /** The pace, the delivery and the niqqud of Inworld start from the settings of the server, unless the user changed them */
    applyInworldDefaults = (effective) => {
        if (!effective || this.inworldTouched) return;
        if (Number.isFinite(effective.speakingRate)) this.speakingRate = String(effective.speakingRate);
        if (effective.deliveryMode) this.deliveryMode = effective.deliveryMode;
        if (effective.niqqud) this.niqqud = effective.niqqud;
    };

    get builtQuery() {
        if (this.isInworld) {
            const query = { text: this.text, language: this.language, model: this.model };
            const name = this.inworldName.trim();
            if (name) query.name = name;
            query.speakingRate = Number(this.speakingRate);
            if (this.supportsDeliveryMode) query.deliveryMode = this.deliveryMode;
            if (this.isHebrew) query.niqqud = this.niqqud;
            return query;
        }
        return {
            text: this.text,
            prompt: this.prompt,
            language: this.language,
            name: this.name,
            model: this.model,
        };
    }

    /**
     * Creates one audio file for a test. Nothing is stored on the server.
     * The Gemini 3.8 models answer with a WAV file, Inworld with an MP3 file - the Content-Type tells what the file is.
     * Never throws.
     * @returns {Promise<{ sample?: { url: string, mime: string, ms: number, bytes: number }, error?: { status: number, message: string, detail: string } }>}
     */
    requestAudio = async (query) => {
        const startedAt = Date.now();
        let res;
        try {
            res = await axios.post(urls.ai.textToSpeechGemini, query, { withCredentials: true });
        } catch (err) {
            return { error: describeError(err) };
        }

        // The audio is sent as base64 text, and the Content-Type says what it is: data:audio/wav;base64
        const contentType = String(res.headers?.['content-type'] || '');
        if (!contentType.includes('base64') || typeof res.data !== 'string' || !res.data) {
            return { error: { status: res.status, message: 'Unexpected answer of the server', detail: `Content-Type: ${contentType || '(none)'}` } };
        }
        const mime = getMimeFromContentType(contentType);
        return {
            sample: {
                url: `data:${mime};base64,${res.data}`,
                mime,
                ms: Date.now() - startedAt,
                bytes: Math.floor(res.data.length * 3 / 4),
            },
        };
    };

    generateAudio = async () => {
        if (this.loading) return;
        this.setLoading(true);
        this.error = null;
        const query = this.builtQuery;
        const provider = this.provider;
        const { sample, error } = await this.requestAudio(query);
        runInAction(() => {
            if (error) {
                this.error = error;
            } else {
                this.lastId += 1;
                this.generatedAudios.unshift({ id: this.lastId, ...sample, ...query, provider, createdAt: Date.now() });
            }
            this.loading = false;
        });
    };

    clearAudios = () => {
        this.generatedAudios = [];
        this.error = null;
    };
}

const generateAudioStore = new GenerateAudioStore();
export default generateAudioStore;
